import { getRegistryEntry, type MessageKey } from '../config/messageRegistry';
import { recordMessageState } from '../services/messageState';
import { updateCurrentUserInfo } from '../services/auth';
import { selectMessageStateByKey } from '../slices/messageState/messageStateSlice';
import { store, type RootState } from '../store/store';
import type { User } from '../types/User';

const terminalMessageStatuses = new Set([
  'dismissed',
  'completed',
  'opted_out',
]);

export const hasCompletedTour = (
  state: RootState,
  user: User | null | undefined,
  key: MessageKey
): boolean => {
  const messageState = selectMessageStateByKey(state, key);

  if (messageState) {
    return (
      messageState.status === 'completed' ||
      messageState.status === 'in_progress'
    );
  }

  return user?.onboardingTips?.[key] === true;
};

/**
 * `anyVersion` counts a terminal row at any version as dismissed. Use it for
 * gates that only need "the user has seen this tip at some point" (e.g. the
 * feedback modals waiting on 'promote-referral'), so a version bump that
 * re-prompts the tip does not also block them.
 */
export const isTipDismissed = (
  state: RootState,
  user: User | null | undefined,
  key: MessageKey,
  options?: { anyVersion?: boolean }
): boolean => {
  const messageState = selectMessageStateByKey(state, key);

  if (messageState) {
    return (
      terminalMessageStatuses.has(messageState.status) &&
      (options?.anyVersion === true ||
        messageState.version >= getRegistryEntry(key).currentVersion)
    );
  }

  return user?.onboardingTips?.[key] === true;
};

const updateLegacyOnboardingTips = async (
  keys: readonly MessageKey[]
): Promise<void> => {
  const user = store.getState().user.profile;

  if (!user) {
    return;
  }

  await updateCurrentUserInfo({
    onboardingTips: keys.reduce<Record<string, boolean>>(
      (onboardingTips, key) => {
        onboardingTips[key] = true;
        return onboardingTips;
      },
      { ...user.onboardingTips }
    ),
  });
};

export const markTourStarted = async (
  key: MessageKey,
  legacyKeys: readonly MessageKey[] = [key]
): Promise<void> => {
  await recordMessageState({
    messageKey: key,
    status: 'in_progress',
    recordImpression: true,
  });

  // Mirror the legacy behavior of writing the "seen" flag on display: without
  // this, an abandoned tour has only a normalized `in_progress` row and would
  // reappear on an older/cached frontend during rollout (and, for preclinical,
  // the sibling variant would not be suppressed). Legacy stays the durable
  // fallback for gating if the normalized write is lost/stale.
  await updateLegacyOnboardingTips(legacyKeys);
};

export const markTourCompleted = async (
  key: MessageKey,
  legacyKeys: readonly MessageKey[] = [key]
): Promise<void> => {
  await recordMessageState({
    messageKey: key,
    status: 'completed',
    version: getRegistryEntry(key).currentVersion,
  });

  await updateLegacyOnboardingTips(legacyKeys);
};

export const markTipSeen = async (key: MessageKey): Promise<void> => {
  await recordMessageState({
    messageKey: key,
    status: 'dismissed',
    version: getRegistryEntry(key).currentVersion,
  });

  await updateLegacyOnboardingTips([key]);
};

// Record a dismissal in the normalized store ONLY (no onboardingTips legacy
// write). For surfaces whose legacy fallback isn't onboardingTips — e.g. the
// dynamic-namespace dismissals (institutional-expired via localStorage; the
// discount banner is reserved pending a stable instance id, ORA-188) — the
// caller performs its own legacy write alongside this.
export const recordTipDismissed = async (key: MessageKey): Promise<void> => {
  await recordMessageState({
    messageKey: key,
    status: 'dismissed',
    version: getRegistryEntry(key).currentVersion,
  });
};

// --- Anki-sync tip (legacy fallback is the `hasDismissedAnkiSync` column, not
// an onboardingTips key, so it needs its own dual-read/dual-write) ---

export const isAnkiSyncDismissed = (
  state: RootState,
  user: User | null | undefined
): boolean => {
  const messageState = selectMessageStateByKey(state, 'anki-sync-dismissed');

  if (messageState) {
    return (
      terminalMessageStatuses.has(messageState.status) &&
      messageState.version >=
        getRegistryEntry('anki-sync-dismissed').currentVersion
    );
  }

  return user?.hasDismissedAnkiSync === true;
};

export const markAnkiSyncDismissed = async (): Promise<void> => {
  await recordMessageState({
    messageKey: 'anki-sync-dismissed',
    status: 'dismissed',
    version: getRegistryEntry('anki-sync-dismissed').currentVersion,
  });

  // Legacy column dual-write (durable fallback during rollout).
  await updateCurrentUserInfo({ hasDismissedAnkiSync: true });
};

// --- Session-feedback prompts (recurring, cooldown-gated) ---
// Cooldown is driven by `lastShownAt`. Dual-read: the normalized row's
// lastShownAt OR the legacy `lastSessionFeedbackDate` JSON, whichever is more
// recent. The global cooldown is the most recent impression across the whole
// `session-feedback:*` family.

export type SessionFeedbackTypeKey =
  'overall' | 'qbank' | 'videos' | 'flashcards';

const SESSION_FEEDBACK_TYPE_KEYS: SessionFeedbackTypeKey[] = [
  'overall',
  'qbank',
  'videos',
  'flashcards',
];

const sessionFeedbackKey = (typeKey: SessionFeedbackTypeKey): MessageKey => {
  switch (typeKey) {
    case 'overall':
      return 'session-feedback:overall';
    case 'qbank':
      return 'session-feedback:qbank';
    case 'videos':
      return 'session-feedback:videos';
    case 'flashcards':
      return 'session-feedback:flashcards';
  }
};

const toDate = (value: string | Date | null | undefined): Date | null => {
  if (!value) {
    return null;
  }
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const latestDate = (dates: (Date | null)[]): Date | null =>
  dates.reduce<Date | null>((latest, date) => {
    if (!date) {
      return latest;
    }
    return !latest || date > latest ? date : latest;
  }, null);

export const getSessionFeedbackTypeLastShown = (
  state: RootState,
  user: User | null | undefined,
  typeKey: SessionFeedbackTypeKey
): Date | null => {
  const normalized = toDate(
    selectMessageStateByKey(state, sessionFeedbackKey(typeKey))?.lastShownAt
  );

  const legacy = toDate(user?.lastSessionFeedbackDate?.types?.[typeKey]);
  return latestDate([normalized, legacy]);
};

export const getSessionFeedbackGlobalLastShown = (
  state: RootState,
  user: User | null | undefined
): Date | null => {
  const normalized = SESSION_FEEDBACK_TYPE_KEYS.map((typeKey) =>
    toDate(
      selectMessageStateByKey(state, sessionFeedbackKey(typeKey))?.lastShownAt
    )
  );

  const legacy = toDate(user?.lastSessionFeedbackDate?.lastFeedback);
  return latestDate([...normalized, legacy]);
};

export const recordSessionFeedbackShown = async (
  typeKey: SessionFeedbackTypeKey
): Promise<void> => {
  await recordMessageState({
    messageKey: sessionFeedbackKey(typeKey),
    status: 'shown',
    recordImpression: true,
  });

  // Legacy dual-write of the lastSessionFeedbackDate cooldown structure.
  const user = store.getState().user.profile;
  const now = new Date();

  await updateCurrentUserInfo({
    lastSessionFeedbackDate: {
      lastFeedback: now,
      types: {
        ...(user?.lastSessionFeedbackDate?.types ?? {}),
        [typeKey]: now,
      },
    },
  });
};
